import { Button } from "@/components/common/Button";
import { useUserStore } from "@/modules/auth/store/user";
import { formatEllipsis } from "@/modules/auth/utils/format";
import { useTonProofPayload } from "@/modules/wallet/hooks/useTonProofPayload";
import { useTonWalletProof } from "@/modules/wallet/hooks/useTonWalletProof";
import { AnalyticsEvents } from "@/shared/utils/analytics/events";
import { Address } from "@ton/core";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

const ConnectedWallet = () => {
  const { t } = useTranslation();
  const { wallet, setWalletBalance } = useUserStore();
  const location = useLocation();

  useTonWalletProof();
  useTonProofPayload();

  const [tonConnectUI] = useTonConnectUI();

  const handleDisconnect = () => {
    tonConnectUI.disconnect();
    setWalletBalance(0.0);
  };

  const handleConnect = () => {
    tonConnectUI.openModal();
  };

  useEffect(() => {
    const connectedAddress = tonConnectUI.account?.address;
    const walletAddress = wallet?.address;

    if (connectedAddress && walletAddress) {
      const normalizedConnected = Address.parse(connectedAddress).toRawString();
      const normalizedWallet = Address.parse(walletAddress).toRawString();

      if (normalizedConnected !== normalizedWallet) {
        tonConnectUI.disconnect();
        setWalletBalance(0.0);
      }
    }
  }, [tonConnectUI, wallet?.address, setWalletBalance]);

  const analyticsEvent = location.pathname.startsWith("/airdrop")
      ? AnalyticsEvents.ClaimAirdropPage.ConnectWallet
      : location.pathname.startsWith("/settings")
      ? AnalyticsEvents.Settings.ConnectWallet
      : AnalyticsEvents.WithdrawRewardsPage.ConnectWallet;

  return (
    <div className="flex w-full flex-col">
      <p className="p-4 pb-2 text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter uppercase">
        {t("deposit-page.connected_wallet")}
      </p>
      <div className="flex items-center justify-between bg-bg rounded-[0.75rem] px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <p className="text-[1.0625rem] leading-[1.375rem] text-text">
            {tonConnectUI.account?.address
              ? t("settings.connected")
              : t("settings.no_connected")}
          </p>
          {tonConnectUI.account?.address && (
            <p className="text-[0.9375rem] leading-[1.25rem] font-semibold text-subtitle-text">
              {formatEllipsis(
                Address.parse(tonConnectUI.account.address).toString({
                  bounceable: false,
                })
              )}
            </p>
          )}
        </div>

        {tonConnectUI.account?.address ? (
          <Button
            onClick={handleDisconnect}
            text={t("main-page.disconnect_wallet")}
            size="small"
            mode="bezeled"
            pilled
          />
        ) : (
          <Button
            onClick={handleConnect}
            text={t("main-page.connect_wallet")}
            size="small"
            mode="bezeled"
            pilled
            analyticsEvent={analyticsEvent}
          />
        )}
      </div>
    </div>
  );
};

export default ConnectedWallet;
